import { useDebounceFn } from '@vueuse/core'

import { persistLeftDrawerWidth } from '@app/shared/api/user'

// Stimulus-to-Slim boundary for the resizable left drawer: element lookups
// against the server-rendered layout live here. The drawer is sized in pixels
// via a CSS variable on `<body>`, server-rendered inline from the persisted
// preference, overwritten by `setWidthPx` in-session, and falling back to the
// responsive default in `two-drawer-layout-new.scss` when unset. It belongs on
// `<body>` because docked persistent modals render outside `#main-wrapper` and
// derive their offset from the same resolved width.
const CSS_VARIABLE = '--left-drawer-user-width'
export const LEFT_DRAWER_ID = 'left-drawer'
// Bounds must match UserPreferencesContract::LEFT_DRAWER_WIDTH_RANGE and the
// clamp() in two-drawer-layout-new.scss.
export const LEFT_DRAWER_MIN_WIDTH_PX = 200
export const LEFT_DRAWER_MAX_WIDTH_PX = 480
// Persistence is fire-and-forget, once per drag and once per keyboard step, so
// this absorbs a held arrow key. It never gates the live, on-pointermove resize.
const PERSIST_DEBOUNCE_MS = 150

function getDrawer(): HTMLElement | null {
  return document.getElementById(LEFT_DRAWER_ID)
}

function clampPx(px: number): number {
  return Math.min(
    LEFT_DRAWER_MAX_WIDTH_PX,
    Math.max(LEFT_DRAWER_MIN_WIDTH_PX, Math.round(px))
  )
}

const schedulePersist = useDebounceFn((px: number) => {
  void persistLeftDrawerWidth(px)
}, PERSIST_DEBOUNCE_MS)

// Returns the clamped value it applied so callers can track it without
// re-measuring the DOM.
export function setWidthPx(px: number, options: { persist: boolean }): number {
  const clamped = clampPx(px)
  document.body.style.setProperty(CSS_VARIABLE, `${clamped}px`)
  if (options.persist) void schedulePersist(clamped)
  return clamped
}

// Current drawer width in pixels, or null while the layout is absent.
export function readWidthPx(): number | null {
  const drawer = getDrawer()
  if (!drawer) return null
  return Math.round(drawer.getBoundingClientRect().width)
}

// Snapshots the drag geometry, the drawer's left edge, which is stable while a
// drag lasts. The returned converter is then pure math, with no layout reads on
// pointermove.
export function beginDragMeasurement(): ((clientX: number) => number) | null {
  const drawer = getDrawer()
  if (!drawer) return null
  const drawerLeft = drawer.getBoundingClientRect().left
  return (clientX) => clientX - drawerLeft
}
